import { useMemo } from 'react';

import {
  DXF_MAPPING_ROLE,
  type DxfEntityKey,
  type DxfMappingValidationResult,
} from '@frontend/api';

import {
  entityKeyHasVisibleChunk,
  selectionEntityKeys,
} from 'modules/common/viewer/dxfViewer/session/mappingModel';
import {
  groupMappingValidationIssues,
  type MappingIssueEntityRow,
  type MappingIssueGroup,
} from 'modules/common/viewer/dxfViewer/session/mappingValidation';
import { mappingRoleDefinition } from 'modules/common/viewer/dxfViewer/session/roles';
import type {
  DrawingModel,
  DxfViewerSession,
  MappingAssignments,
} from 'modules/common/viewer/dxfViewer/session/types';
import { useReadableStore } from 'modules/common/viewer/dxfViewer/session/useSessionState';

/** e.g. `"LINE | Bend"` — the entity's DXF type and its current role(s). */
const entityLabel = (
  key: DxfEntityKey,
  model: DrawingModel,
  assignments: MappingAssignments | null,
): string => {
  const types = model.entityTypesByKey.get(key);
  // A key can in principle span more than one source type (see
  // `entityTypesByKey`'s own doc comment) — join rather than pick one
  const typeLabel = types && types.size > 0 ? [...types].join('/') : 'Entity';
  // Unmapped defaults to Cut
  const roles = assignments?.get(key) ?? [DXF_MAPPING_ROLE.CUTTING];
  const roleLabels = roles.map(
    (role) => mappingRoleDefinition(role)?.label ?? role,
  );
  return [typeLabel, ...roleLabels].join(' | ');
};

export const useDxfMappingIssueGroups = (
  session: DxfViewerSession,
  model: DrawingModel,
  validationResult: DxfMappingValidationResult,
): readonly MappingIssueGroup[] => {
  const hiddenLayers = useReadableStore(session.hiddenLayers);
  const selection = useReadableStore(session.selection);
  const mapping = useReadableStore(session.mapping);
  const assignments = mapping.draft ?? mapping.saved;

  return useMemo(() => {
    const selectedKeys = selectionEntityKeys(selection, model);
    const rawGroups = groupMappingValidationIssues(validationResult);

    return rawGroups.map((raw): MappingIssueGroup => {
      const entities: MappingIssueEntityRow[] = raw.entityKeys.map((key) => {
        const isSelectable = entityKeyHasVisibleChunk(key, model, hiddenLayers);
        return {
          key,
          label: entityLabel(key, model, assignments),
          isSelectable,
          isSelected: isSelectable && selectedKeys.has(key),
        };
      });

      const selectableEntities = entities.filter(
        (entity) => entity.isSelectable,
      );

      return {
        id: raw.id,
        message: raw.message,
        count: entities.length,
        isSelectable: selectableEntities.length > 0,
        isSelected:
          selectableEntities.length > 0 &&
          selectableEntities.every((entity) => entity.isSelected),
        entities,
      };
    });
  }, [validationResult, model, hiddenLayers, selection, assignments]);
};
